
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { useFormData } from "@/stores/formStore";
import LogoWithBurst from "./LogoWithBurst";
import LanguageSwitcher from "./LanguageSwitcher";

interface HeaderProps {
  children?: React.ReactNode;
  transparent?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  children,
  transparent = false,
}) => {
  const { debugMode, setDebugMode } = useFormData();
  const [searchParams] = useSearchParams();
  const [showDebugToggle, setShowDebugToggle] = useState(false);
  const { t } = useTranslation("nav");

  useEffect(() => {
    if (searchParams.get("debug") === "true") {
      setShowDebugToggle(true);
    }
  }, [searchParams]);

  // Also show toggle if debug mode is already on (e.g. from persisted state)
  useEffect(() => {
    if (debugMode) setShowDebugToggle(true);
  }, [debugMode]);

  return (
    <header className={`${transparent ? 'bg-transparent border-transparent' : 'bg-card border-border'} flex w-full items-center gap-6 overflow-hidden flex-wrap py-4 px-8 border-b max-md:max-w-full max-md:px-5 font-antiqua`}>
      <Link to="/" className="group flex items-center gap-2" aria-label={t("homeAria")}>
        <LogoWithBurst
          src="/lovable-uploads/42db7210-adf1-4d43-9443-969c94fa2bb2.png"
          alt={t("logoAlt")}
          size={40}
          className="shrink-0 transition-opacity duration-300 group-hover:opacity-80"
          imgClassName="transition-[filter,opacity] duration-300 group-hover:grayscale group-hover:brightness-110 group-hover:opacity-80"
          showBurst={false}
        />
        <span className="text-white text-2xl font-medium group-hover:text-foreground/80 transition-colors">
          Ink &amp; Insanity
        </span>
      </Link>
      <div className="ml-auto flex items-center gap-3">
        {showDebugToggle && (
          <>
            {debugMode && (
              <Link
                to="/data-explorer"
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground/80 hover:text-white hover:bg-secondary transition-colors"
              >
                {t("dataExplorer")}
              </Link>
            )}
            <label htmlFor="debug-mode-toggle" className="text-sm text-foreground/80 select-none">
              {t("debugMode")}
            </label>
            <Switch
              id="debug-mode-toggle"
              checked={debugMode}
              onCheckedChange={setDebugMode}
              aria-label={t("debugToggleAria")}
              className="data-[state=checked]:bg-brand-cream data-[state=unchecked]:bg-border"
            />
          </>
        )}
        <LanguageSwitcher />
      </div>
      {children}
    </header>
  );
};

export default Header;
